'use client';

import { useRef, type ReactNode } from 'react';

/**
 * Softly reveals one live route tree. We deliberately do not overlap whole pages: overlapping client
 * pages duplicates their data hooks and heavy scenes (Settings briefly created two WebGL mascots).
 *
 * The path arrives as a prop from the Shell rather than from `usePathname()`: an intercepted page
 * overlay changes the ADDRESS without touching this tree, and re-keying on the address would remount
 * the mounted page — refetching its data and replaying the scrim — while the reader only opened a
 * modal over it.
 *
 * A plugin page owns its own sub-addresses (`/p/kanban/<board>/<card>`): moving between them is the page
 * navigating within itself, so they share one key (`routePageIdentity`). Re-keying on the full path would
 * remount the plugin's page for every opened card, refetching its data, replaying the scrim and dropping
 * the scroll position the reader was holding.
 *
 * The reveal dims the incoming route with a black scrim that fades out instead of animating the
 * content's own opacity. Fractional opacity on the route tree makes Chromium re-composite the
 * blurred mascot glows, which visibly brightens the whole hero before it settles back to black;
 * a solid black scrim can only darken, so the fade stays smooth on the OLED theme.
 *
 * The scrim's mode is CSS, read from the document's `data-effects` attribute (styles/animations.css),
 * never from the effects context. The server cannot know the reader's preference — it always serves
 * the default mode and the head script resolves the real one before paint — so a scrim whose opacity
 * or presence React decided would disagree with the served markup on every load for a reader whose OS
 * reduces motion or who switched effects off. That is a hydration mismatch, and the whole tree
 * re-renders for it; the attribute is already correct at paint, so the visible result is unchanged.
 */
/** The identity of the page a path belongs to: `/p/<plugin>/...` is the plugin's one page whatever its
 *  sub-address; every other path is its own page. */
function routePageIdentity(pathname: string | null): string | null {
  if (!pathname?.startsWith('/p/')) return pathname;
  return `/p/${pathname.slice(3).split('/')[0]}`;
}

export function RouteTransition({ children, pathname }: { children: ReactNode; pathname: string | null }) {
  const page = routePageIdentity(pathname);
  const routeSequence = useRef(0);
  const previousPage = useRef(page);
  if (previousPage.current !== page) {
    routeSequence.current += 1;
    previousPage.current = page;
  }
  // The sequence matters when a user returns to the same route before its previous instance has
  // finished exiting (Stats → Settings → Stats): a pathname-only key would hand the returning page the
  // element the leaving one is still fading, and the scrim would not replay.
  const routeKey = `${page}:${routeSequence.current}`;

  return (
    <div className="relative h-full">
      <div key={routeKey} data-testid="route-transition" className="h-full">
        {children}
      </div>
      {/* Remounted per route by its key, so the CSS fade replays on every navigation. Quiet modes are
          handled by the attribute-keyed rules: `off` stops the animation, leaving the declared opacity
          0, and `reduced` starts lower and finishes sooner. */}
      <div
        key={`scrim:${routeKey}`}
        aria-hidden
        className="route-scrim pointer-events-none absolute inset-0 z-[var(--z-lift)] bg-background"
      />
    </div>
  );
}
